import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, Tenant } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';

export function AdminBillingPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [costModalEnv, setCostModalEnv] = useState<'dev' | 'prod'>('dev');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [planType, setPlanType] = useState<'per-label' | 'flat-rate'>('per-label');

  const { data: tenants } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.getTenants(),
  });

  const { data: pricingPlans, isLoading: plansLoading } = useQuery({
    queryKey: ['admin', 'pricingPlans'],
    queryFn: () => adminApi.getPricingPlans(),
  });

  const { data: billingData } = useQuery({
    queryKey: ['admin', 'billing', selectedTenant?.tenantId, selectedMonth],
    queryFn: () => selectedTenant 
      ? adminApi.getBillingReport(selectedTenant.tenantId, selectedMonth)
      : Promise.resolve(null),
    enabled: !!selectedTenant,
  });

  const { data: billingSummary } = useQuery({
    queryKey: ['admin', 'billing-summary', selectedMonth],
    queryFn: () => adminApi.getBillingSummary(selectedMonth),
  });

  const { data: azureCosts } = useQuery({
    queryKey: ['admin', 'azure-costs', selectedMonth],
    queryFn: () => adminApi.getAzureCosts(selectedMonth),
  });

  const createPlanMutation = useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      planType: 'per-label' | 'flat-rate';
      perLabelRate: number;
      perTemplateMonthlyFee: number;
      monthlyFlatRate: number;
      includedLabels: number;
      includedTemplates: number;
    }) => adminApi.createPricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pricingPlans'] });
      setShowCreatePlanModal(false);
      addToast({ type: 'success', message: 'Pricing plan created' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to create plan: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const upsertCostMutation = useMutation({
    mutationFn: (data: {
      month: string;
      environment: 'dev' | 'prod';
      functionsCost: number;
      storageCost: number;
      appInsightsCost: number;
      staticWebAppCost: number;
      otherCost: number;
      notes?: string;
    }) => adminApi.updateAzureCost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'azure-costs'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      setShowCostModal(false);
      addToast({ type: 'success', message: 'Azure costs updated' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to save costs: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const handleCreatePlan = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const pt = formData.get('planType') as 'per-label' | 'flat-rate';
    createPlanMutation.mutate({
      name: formData.get('name') as string,
      description: formData.get('description') as string || '',
      planType: pt,
      perLabelRate: pt === 'per-label' ? Math.round(parseFloat(formData.get('perLabelRate') as string || '0') * 100) : 0,
      perTemplateMonthlyFee: Math.round(parseFloat(formData.get('perTemplateMonthlyFee') as string || '0') * 100),
      monthlyFlatRate: pt === 'flat-rate' ? Math.round(parseFloat(formData.get('monthlyFlatRate') as string || '0') * 100) : 0,
      includedLabels: pt === 'per-label' ? parseInt(formData.get('includedLabels') as string || '0', 10) : 0,
      includedTemplates: parseInt(formData.get('includedTemplates') as string || '0', 10),
    });
  };

  const handleSaveCosts = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    upsertCostMutation.mutate({
      month: selectedMonth,
      environment: costModalEnv,
      functionsCost: Math.round(parseFloat(formData.get('functionsCost') as string || '0') * 100),
      storageCost: Math.round(parseFloat(formData.get('storageCost') as string || '0') * 100),
      appInsightsCost: Math.round(parseFloat(formData.get('appInsightsCost') as string || '0') * 100),
      staticWebAppCost: Math.round(parseFloat(formData.get('staticWebAppCost') as string || '0') * 100),
      otherCost: Math.round(parseFloat(formData.get('otherCost') as string || '0') * 100),
      notes: formData.get('notes') as string || undefined,
    });
  };

  /** Format cents as USD */
  const formatCents = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      const data = await adminApi.getBillingExport(selectedMonth, format, selectedTenant?.tenantId);
      const blob = new Blob(
        [typeof data === 'string' ? data : JSON.stringify(data, null, 2)],
        { type: format === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const tenantSuffix = selectedTenant ? `-${selectedTenant.name.replace(/\s+/g, '-').toLowerCase()}` : '';
      a.download = `billing${tenantSuffix}-${selectedMonth}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast({ type: 'error', message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setExporting(false);
    }
  };

  // Margin calculations
  const totalRevenue = billingSummary?.totalRevenue ?? 0;
  const totalAzureCost = azureCosts?.combinedTotal ?? 0;
  const hasAzureCosts = azureCosts && (azureCosts.environments.dev !== null || azureCosts.environments.prod !== null);
  const margin = hasAzureCosts ? totalRevenue - totalAzureCost : null;
  const marginPercent = hasAzureCosts && totalRevenue > 0 ? ((totalRevenue - totalAzureCost) / totalRevenue) * 100 : null;

  const getMarginColor = (pct: number | null) => {
    if (pct === null) return 'text-gray-400';
    if (pct >= 50) return 'text-green-600';
    if (pct >= 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const openCostModal = (env: 'dev' | 'prod') => {
    setCostModalEnv(env);
    setShowCostModal(true);
  };

  // Pre-fill values for cost modal (convert cents to dollars)
  const getCostDefaults = (env: 'dev' | 'prod') => {
    const data = env === 'dev' ? azureCosts?.environments.dev : azureCosts?.environments.prod;
    if (!data) return { functionsCost: '', storageCost: '', appInsightsCost: '', staticWebAppCost: '', otherCost: '', notes: '' };
    return {
      functionsCost: (data.functionsCost / 100).toFixed(2),
      storageCost: (data.storageCost / 100).toFixed(2),
      appInsightsCost: (data.appInsightsCost / 100).toFixed(2),
      staticWebAppCost: (data.staticWebAppCost / 100).toFixed(2),
      otherCost: (data.otherCost / 100).toFixed(2),
      notes: data.notes || '',
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Pricing</h1>
        <div className="flex space-x-2">
          <input
            type="month"
            className="input w-40"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            disabled={exporting}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Export JSON
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreatePlanModal(true)}
          >
            + Create Plan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500">Active Tenants</p>
          <p className="text-2xl font-bold text-gray-900">
            {tenants?.filter(t => t.status === 'active').length || 0}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Pricing Plans</p>
          <p className="text-2xl font-bold text-gray-900">
            {pricingPlans?.length || 0}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Month Revenue (Est.)</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCents(totalRevenue)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Total Labels</p>
          <p className="text-2xl font-bold text-primary-600">
            {billingSummary?.totalLabels?.toLocaleString() ?? '0'}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Margin</p>
          <p className={`text-2xl font-bold ${getMarginColor(marginPercent)}`}>
            {margin !== null ? formatCents(margin) : '—'}
          </p>
          {marginPercent !== null && (
            <p className={`text-xs ${getMarginColor(marginPercent)}`}>
              {marginPercent.toFixed(1)}%
            </p>
          )}
          {!hasAzureCosts && (
            <p className="text-xs text-gray-400 mt-1">No cost data</p>
          )}
        </div>
      </div>

      {/* Service Margin Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Service Margin — {selectedMonth}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => openCostModal('dev')}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Edit Dev Costs
            </button>
            <button
              onClick={() => openCostModal('prod')}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Edit Prod Costs
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Revenue */}
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-2">Monthly Revenue</p>
            <p className="text-3xl font-bold text-green-700">{formatCents(totalRevenue)}</p>
            <p className="text-xs text-green-600 mt-1">{billingSummary?.tenantCount ?? 0} tenants</p>
          </div>

          {/* Azure Costs */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Azure Infrastructure Costs</p>
            {hasAzureCosts ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Development</span>
                  <span className="font-medium">
                    {azureCosts?.environments.dev ? formatCents(azureCosts.environments.dev.totalCost) : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Production</span>
                  <span className="font-medium">
                    {azureCosts?.environments.prod ? formatCents(azureCosts.environments.prod.totalCost) : '—'}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between text-sm">
                  <span className="font-medium text-gray-700">Total</span>
                  <span className="font-bold">{formatCents(totalAzureCost)}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-400 text-sm mb-2">No cost data entered</p>
                <button
                  onClick={() => openCostModal('dev')}
                  className="text-primary-600 text-sm hover:underline"
                >
                  Enter costs
                </button>
              </div>
            )}
          </div>

          {/* Margin */}
          <div className={`p-4 rounded-lg ${
            marginPercent !== null
              ? marginPercent >= 50 ? 'bg-green-50' : marginPercent >= 20 ? 'bg-yellow-50' : 'bg-red-50'
              : 'bg-gray-50'
          }`}>
            <p className="text-sm font-medium text-gray-700 mb-2">Net Margin</p>
            {margin !== null ? (
              <>
                <p className={`text-3xl font-bold ${getMarginColor(marginPercent)}`}>
                  {formatCents(margin)}
                </p>
                <p className={`text-sm mt-1 ${getMarginColor(marginPercent)}`}>
                  {marginPercent!.toFixed(1)}% margin
                </p>
                {/* Visual bar */}
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      marginPercent! >= 50 ? 'bg-green-500' : marginPercent! >= 20 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(Math.max(marginPercent!, 0), 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-gray-400 text-lg">—</p>
            )}
          </div>
        </div>

        {/* Cost Breakdown Table */}
        {hasAzureCosts && (
          <div className="mt-4 border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left py-2 px-4 font-medium text-gray-600">Service</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-600">Dev</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-600">Prod</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {(['functionsCost', 'storageCost', 'appInsightsCost', 'staticWebAppCost', 'otherCost'] as const).map((key) => {
                  const labels: Record<string, string> = {
                    functionsCost: 'Azure Functions',
                    storageCost: 'Storage Account',
                    appInsightsCost: 'App Insights',
                    staticWebAppCost: 'Static Web App',
                    otherCost: 'Other',
                  };
                  const devVal = azureCosts?.environments.dev?.[key] ?? 0;
                  const prodVal = azureCosts?.environments.prod?.[key] ?? 0;
                  return (
                    <tr key={key} className="border-b">
                      <td className="py-2 px-4 text-gray-600">{labels[key]}</td>
                      <td className="py-2 px-4 text-right">{formatCents(devVal)}</td>
                      <td className="py-2 px-4 text-right">{formatCents(prodVal)}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCents(devVal + prodVal)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50">
                  <td className="py-2 px-4 font-semibold">Total</td>
                  <td className="py-2 px-4 text-right font-semibold">
                    {formatCents(azureCosts?.environments.dev?.totalCost ?? 0)}
                  </td>
                  <td className="py-2 px-4 text-right font-semibold">
                    {formatCents(azureCosts?.environments.prod?.totalCost ?? 0)}
                  </td>
                  <td className="py-2 px-4 text-right font-bold">
                    {formatCents(totalAzureCost)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pricing Plans */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Plans</h2>
          {plansLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {pricingPlans?.map((plan) => {
                const isPlanFlatRate = (plan.planType ?? 'per-label') === 'flat-rate';
                return (
                  <div key={plan.planId} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium">{plan.name}</h3>
                        {plan.description && (
                          <p className="text-xs text-gray-400 mt-0.5">{plan.description}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        isPlanFlatRate
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {isPlanFlatRate ? 'Unlimited' : 'Per Label'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {isPlanFlatRate ? (
                        <>
                          <div>
                            <span className="text-gray-500">Flat Rate:</span>
                            <span className="ml-2 font-medium">{formatCents(plan.monthlyFlatRate ?? 0)}/mo</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Labels:</span>
                            <span className="ml-2 font-medium text-green-600">Unlimited</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <span className="text-gray-500">Per Label:</span>
                            <span className="ml-2 font-medium">{formatCents(plan.perLabelRate)}</span>
                          </div>
                          {plan.includedLabels > 0 && (
                            <div>
                              <span className="text-gray-500">Included:</span>
                              <span className="ml-2 font-medium">{plan.includedLabels.toLocaleString()} labels</span>
                            </div>
                          )}
                        </>
                      )}
                      {plan.perTemplateMonthlyFee > 0 && (
                        <div>
                          <span className="text-gray-500">Per Template:</span>
                          <span className="ml-2 font-medium">{formatCents(plan.perTemplateMonthlyFee)}/mo</span>
                        </div>
                      )}
                      {plan.includedTemplates > 0 && (
                        <div>
                          <span className="text-gray-500">Free Templates:</span>
                          <span className="ml-2 font-medium">{plan.includedTemplates}</span>
                        </div>
                      )}
                    </div>
                    {!plan.isActive && (
                      <p className="text-xs text-red-500 mt-2">Inactive</p>
                    )}
                  </div>
                );
              })}
              {(!pricingPlans || pricingPlans.length === 0) && (
                <p className="text-gray-500 text-center py-8">No pricing plans configured</p>
              )}
            </div>
          )}
        </div>

        {/* Tenant Billing */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tenant Billing</h2>
          
          <div className="flex space-x-4 mb-4">
            <select
              className="input flex-1"
              value={selectedTenant?.tenantId || ''}
              onChange={(e) => {
                const tenant = tenants?.find(t => t.tenantId === e.target.value);
                setSelectedTenant(tenant || null);
              }}
            >
              <option value="">Select tenant...</option>
              {tenants?.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          {selectedTenant && billingData ? (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-1">{selectedTenant.name}</h3>
                <p className="text-sm text-gray-500">
                  Plan: {pricingPlans?.find(p => p.planId === selectedTenant.pricingPlanId)?.name || selectedTenant.pricingPlanId}
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-4 text-gray-500">Total Labels</td>
                      <td className="py-2 px-4 text-right font-medium">
                        {billingData.totalLabels?.toLocaleString() || 0}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 text-gray-500">Label Charges</td>
                      <td className="py-2 px-4 text-right font-medium">
                        {formatCents(billingData.labelCharges || billingData.totalAmount || 0)}
                      </td>
                    </tr>
                    {billingData.templateCharges != null && billingData.templateCharges > 0 && (
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-500">Template Charges</td>
                        <td className="py-2 px-4 text-right font-medium">
                          {formatCents(billingData.templateCharges)}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 font-semibold">Total</td>
                      <td className="py-3 px-4 text-right font-bold text-lg">
                        {formatCents(billingData.totalAmount || 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Line Items */}
              {billingData.lineItems && billingData.lineItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Line Items</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="text-left py-1.5 px-3">Template</th>
                          <th className="text-left py-1.5 px-3">Type</th>
                          <th className="text-right py-1.5 px-3">Labels</th>
                          <th className="text-right py-1.5 px-3">Rate</th>
                          <th className="text-right py-1.5 px-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingData.lineItems.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="py-1.5 px-3 font-mono">{item.templateKey}</td>
                            <td className="py-1.5 px-3">{item.labelType}</td>
                            <td className="py-1.5 px-3 text-right">{item.labelCount.toLocaleString()}</td>
                            <td className="py-1.5 px-3 text-right">{formatCents(item.rate)}</td>
                            <td className="py-1.5 px-3 text-right">{formatCents(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : selectedTenant ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Select a tenant to view billing</p>
          )}
        </div>
      </div>

      {/* Tenant Overview Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Tenants — {selectedMonth}</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">Tenant</th>
              <th className="text-left py-2 px-4">Plan</th>
              <th className="text-right py-2 px-4">Labels</th>
              <th className="text-right py-2 px-4">Est. Charge</th>
              <th className="text-left py-2 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {tenants?.map((tenant) => {
              const tenantBilling = billingSummary?.byTenant?.find(b => b.tenantId === tenant.tenantId);
              const tenantPlan = pricingPlans?.find(p => p.planId === tenant.pricingPlanId);
              return (
                <tr key={tenant.tenantId} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium">{tenant.name}</td>
                  <td className="py-2 px-4 text-gray-500">
                    {tenantPlan?.name || tenant.pricingPlanId}
                    {tenantPlan && (tenantPlan.planType ?? 'per-label') === 'flat-rate' && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">∞</span>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">{tenantBilling?.labels?.toLocaleString() ?? '—'}</td>
                  <td className="py-2 px-4 text-right">{tenantBilling ? formatCents(tenantBilling.charge) : '—'}</td>
                  <td className="py-2 px-4">
                    <span className={`px-2 py-1 text-xs rounded ${
                      tenant.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Pricing Plan Modal */}
      {showCreatePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Pricing Plan</h2>
            <form onSubmit={handleCreatePlan} className="space-y-4">
              <div>
                <label className="label">Plan Name</label>
                <input name="name" className="input" required placeholder="e.g. Starter Plan" />
              </div>
              <div>
                <label className="label">Description</label>
                <input name="description" className="input" placeholder="Optional description" />
              </div>
              <div>
                <label className="label">Plan Type</label>
                <div className="flex space-x-4 mt-1">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="planType"
                      value="per-label"
                      checked={planType === 'per-label'}
                      onChange={() => setPlanType('per-label')}
                      className="text-primary-600"
                    />
                    <span className="text-sm">Per Label</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="planType"
                      value="flat-rate"
                      checked={planType === 'flat-rate'}
                      onChange={() => setPlanType('flat-rate')}
                      className="text-primary-600"
                    />
                    <span className="text-sm">Flat Rate (Unlimited Labels)</span>
                  </label>
                </div>
              </div>

              {planType === 'per-label' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Per Label Rate ($)</label>
                    <input
                      name="perLabelRate"
                      type="number"
                      step="0.0001"
                      className="input"
                      required
                      placeholder="0.01"
                    />
                  </div>
                  <div>
                    <label className="label">Included Labels</label>
                    <input
                      name="includedLabels"
                      type="number"
                      className="input"
                      placeholder="0"
                      defaultValue="0"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">Monthly Flat Rate ($)</label>
                  <input
                    name="monthlyFlatRate"
                    type="number"
                    step="0.01"
                    className="input"
                    required
                    placeholder="99.00"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Per Template Fee ($/mo)</label>
                  <input
                    name="perTemplateMonthlyFee"
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="0.00"
                    defaultValue="0"
                  />
                </div>
                <div>
                  <label className="label">Included Templates</label>
                  <input
                    name="includedTemplates"
                    type="number"
                    className="input"
                    placeholder="0"
                    defaultValue="0"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowCreatePlanModal(false); setPlanType('per-label'); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createPlanMutation.isPending}
                >
                  {createPlanMutation.isPending ? 'Creating...' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Azure Cost Entry Modal */}
      {showCostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-1">Enter Azure Costs</h2>
            <p className="text-sm text-gray-500 mb-4">
              {selectedMonth} — <span className="font-medium capitalize">{costModalEnv}</span> environment
            </p>
            <form onSubmit={handleSaveCosts} className="space-y-3">
              <div>
                <label className="label">Azure Functions ($)</label>
                <input
                  name="functionsCost"
                  type="number"
                  step="0.01"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).functionsCost}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">Storage Account ($)</label>
                <input
                  name="storageCost"
                  type="number"
                  step="0.01"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).storageCost}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">Application Insights ($)</label>
                <input
                  name="appInsightsCost"
                  type="number"
                  step="0.01"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).appInsightsCost}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">Static Web App ($)</label>
                <input
                  name="staticWebAppCost"
                  type="number"
                  step="0.01"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).staticWebAppCost}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">Other ($)</label>
                <input
                  name="otherCost"
                  type="number"
                  step="0.01"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).otherCost}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <input
                  name="notes"
                  className="input"
                  defaultValue={getCostDefaults(costModalEnv).notes}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCostModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={upsertCostMutation.isPending}
                >
                  {upsertCostMutation.isPending ? 'Saving...' : 'Save Costs'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}